/**
 * "A new version of the desk is ready" (the owner, 2026-10-24: an error that flashes for a few seconds).
 *
 * Every deploy renames the screens' files and Cloudflare Pages drops the old ones, so a tab opened before the deploy cannot fetch a
 * screen it had not opened yet. Until now the desk answered by reloading itself, at once, even in the middle of a form. Now:
 *
 *   · the page quietly asks the server, every few minutes and when the tab comes back, which entry script the live index.html
 *     points at; when it is not the one this tab is running, a banner says a new version is ready and lets the person refresh when
 *     they have finished what they are typing (NewVersionBanner);
 *   · a failed preload no longer reloads the page by itself (main.tsx); only a screen that truly cannot open reloads it, once
 *     (RouteError), because the person is already moving to another screen.
 *
 * In development there is no hashed entry script, so nothing is watched.
 */
import { useSyncExternalStore } from 'react';

/** The hashed entry script a built index.html points at ("/assets/index-Bx12.js"), or null (development, or not a desk page). */
export function entryScriptOf(html: string): string | null {
  const m = /<script\b[^>]*\btype=["']module["'][^>]*\bsrc=["'](\/assets\/[^"']+\.js)["']/i.exec(html)
    ?? /<script\b[^>]*\bsrc=["'](\/assets\/[^"']+\.js)["'][^>]*\btype=["']module["']/i.exec(html);
  return m ? m[1]! : null;
}

/** True when the live page runs a different entry script from this tab's. Unknown on either side means no news. */
export function isNewer(running: string | null, live: string | null): boolean {
  return Boolean(running && live && running !== live);
}

let ready = false;
const listeners = new Set<() => void>();

/** Tells the person a new version is ready (also called when a screen file turns out to be gone). Idempotent. */
export function announceNewVersion(): void {
  if (ready) return;
  ready = true;
  listeners.forEach((l) => l());
}

/** Whether this tab already knows a newer version is live (read by RouteError: an error in a tab that is behind is the stale tab's, not the screen's). */
export function newVersionIsReady(): boolean { return ready; }

export function useNewVersionReady(): boolean {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => ready,
    () => false,
  );
}

const EVERY_MS = 5 * 60_000;
const MIN_GAP_MS = 60_000;
let started = false;

/** Starts the watch once. Safe to call in any environment. */
export function startVersionWatch(): void {
  if (started || typeof document === 'undefined') return;
  const running = entryScriptOf(document.documentElement.outerHTML);
  if (!running) return;           // development, or an unhashed build: nothing to compare
  started = true;
  let last = Date.now();
  const check = async () => {
    if (ready || Date.now() - last < MIN_GAP_MS) return;
    last = Date.now();
    try {
      const res = await fetch('/index.html', { cache: 'no-store', credentials: 'omit' });
      if (!res.ok) return;
      if (isNewer(running, entryScriptOf(await res.text()))) announceNewVersion();
    } catch { /* offline or blocked: say nothing */ }
  };
  window.setInterval(() => { void check(); }, EVERY_MS);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void check(); });
}
